import { IMediaItem, IMention, IPostCreatedBy, IPostStatus } from '_interfaces/types/post.types';
import { DocumentData } from 'firebase/firestore';
import {
  ICreateActivityFeedHashtag,
  IGetActivityFeedHashtag,
} from 'redux-store/api/activity-feed/hashtags.types';
import { IPostType, ISystemPostMetadata } from './systemPost.types';

/**
 * Get the best available URL for display from a media item
 * Priority: thumbnail > medium > high > original > url
 */
export function getMediaDisplayUrl(
  media: IMediaItem,
  quality?: 'thumbnail' | 'medium' | 'high'
): string {
  if (!media.transcoded) {
    return media.url;
  }

  if (quality && media.transcoded[quality]) {
    return media.transcoded[quality]!;
  }

  return (
    media.transcoded.thumbnail ||
    media.transcoded.medium ||
    media.transcoded.high ||
    media.transcoded.original ||
    media.url
  );
}

/**
 * Get the original URL or the best transcoded version
 */
export function getMediaOriginalUrl(media: IMediaItem): string {
  return media.transcoded?.original || media.url;
}

export class IPost {
  static kId = 'id';
  static kCompanyId = 'companyId';
  static kProjectId = 'projectId';
  static kCreatedAt = 'createdAt';
  static kCreatedBy = 'createdBy';
  static kItemId = 'itemId';
  static kItemName = 'itemName';
  static kIsDeleted = 'isDeleted';
  static kIsEdited = 'isEdited';
  static kLikes = 'likes';
  static kMessage = 'message';
  static kMedia = 'media';
  static kReplyCount = 'replyCount';
  static kTags = 'tags';
  static kMentions = 'mentions';
  static kHashtags = 'hashtags';
  static kStatus = 'status';
  static kPostType = 'postType';
  static kSystemMetadata = 'systemMetadata';
  static kVisibility = 'visibility';
  static kParentPath = 'parentPath';

  constructor(
    public readonly id: string | undefined,
    public readonly companyId: string,
    public readonly projectId: string,
    public readonly createdAt: Date,
    public readonly createdBy: IPostCreatedBy,
    public readonly itemId: string,
    public readonly itemName: string,
    public readonly isDeleted: boolean,
    public readonly isEdited: boolean,
    public readonly likes: string[],
    public readonly message: string,
    public readonly media: IMediaItem[],
    public readonly replyCount: number,
    public readonly tags: string[],
    public readonly mentions: IMention[],
    public readonly hashtags: IGetActivityFeedHashtag[],
    public readonly status: IPostStatus,
    public readonly postType: IPostType = IPostType.USER_POST,
    public readonly parentPath: string,
    public readonly systemMetadata?: ISystemPostMetadata,
    public readonly visibility?: string[]
  ) {}

  // Helper method to check if this is a system post
  isSystemPost(): boolean {
    return this.postType !== IPostType.USER_POST;
  }

  static empty() {
    return new IPost(
      '',
      '',
      '',
      new Date(),
      {
        userId: '',
        displayName: '',
        avatarUrl: '',
      },
      '',
      '',
      false,
      false,
      [],
      '',
      [],
      0,
      [],
      [],
      [],
      IPostStatus.DRAFT,
      IPostType.USER_POST,
      '',
      undefined
    );
  }

  static fromDocumentData(data: DocumentData) {
    return new IPost(
      data[IPost.kId],
      data[IPost.kCompanyId],
      data[IPost.kProjectId],
      data[IPost.kCreatedAt]?.toDate?.() || data[IPost.kCreatedAt],
      data[IPost.kCreatedBy],
      data[IPost.kItemId],
      data[IPost.kItemName],
      data[IPost.kIsDeleted],
      data[IPost.kIsEdited],
      data[IPost.kLikes],
      data[IPost.kMessage],
      data[IPost.kMedia],
      data[IPost.kReplyCount],
      data[IPost.kTags],
      data[IPost.kMentions],
      data[IPost.kHashtags],
      data[IPost.kStatus],
      data[IPost.kPostType] || IPostType.USER_POST,
      data[IPost.kParentPath],
      data[IPost.kSystemMetadata],
      data[IPost.kVisibility]
    );
  }
}
